import React, { useMemo } from 'react';
import { ArticlePerformanceRow, MetricsFetchStatus, SortBy, SortOrder } from '../../../../services/performanceMetricsService';
import { PerformanceRow, SortHeader } from './PerformanceRow';

const PAGE_SIZE = 25;

interface PerformanceTableViewProps {
  rows: ArticlePerformanceRow[];
  totalCount: number;
  loading: boolean;
  metricsStatus: MetricsFetchStatus | null;
  sortBy: SortBy;
  order: SortOrder;
  offset: number;
  search: string;
  onSortClick: (col: SortBy) => void;
  onOffsetChange: (offset: number | ((o: number) => number)) => void;
  onSearchChange: (value: string) => void;
  onRowClick: (url: string) => void;
}

export const PerformanceTableView: React.FC<PerformanceTableViewProps> = ({
  rows,
  totalCount,
  loading,
  metricsStatus,
  sortBy,
  order,
  offset,
  search,
  onSortClick,
  onOffsetChange,
  onSearchChange,
  onRowClick,
}) => {
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < totalCount;

  const matchedCount = useMemo(() => rows.filter((r) => r.matched_to_gsc).length, [rows]);
  const indexedCount = useMemo(() => rows.filter((r) => r.is_indexed === true).length, [rows]);
  const indexationLoaded = useMemo(() => rows.some((r) => r.is_indexed != null), [rows]);

  return (
    <>
      {/* Search + summary bar */}
      <div className="flex items-center justify-between mb-3 gap-4">
        <input
          type="text"
          placeholder="Search title or URL…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
        />
        <div className="text-xs text-gray-500 text-right">
          {totalCount > 0 ? (
            <>
              {totalCount} articles
              {metricsStatus?.last_completed_date && <> · GSC through {metricsStatus.last_completed_date}</>}
              {indexationLoaded && <> · {indexedCount}/{rows.length} indexed (this page)</>}
              <> · {matchedCount}/{rows.length} matched (this page)</>
            </>
          ) : (
            'No articles'
          )}
        </div>
      </div>

      {/* Sort headers + rows */}
      <div className="relative">
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 border-b-0 rounded-t text-[10px] font-medium text-gray-500 uppercase tracking-wider">
          <SortHeader label="Article" col="title" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="flex-1 min-w-0" tooltip="Article title from WordPress" />
          <SortHeader label="Category" col="category" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-24 shrink-0" tooltip="WordPress category the article is filed under." />
          <SortHeader label="Pillar" col="pillar" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-20 shrink-0" tooltip="Content pillar this article belongs to. Assigned during pipeline generation or via content inventory enrichment." />
          <SortHeader label="Funnel" col="funnel" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-16 shrink-0" tooltip="Funnel stage: Top (awareness), Medium (consideration), Bottom (decision), or Authority (thought leadership). Determines CTA strategy and keyword targeting." />
          <SortHeader label="Published" col="published" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-20 shrink-0" tooltip="Date the article was first published on WordPress. This never changes even if the article is updated later." />
          <SortHeader label="Updated" col="updated" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-20 shrink-0" tooltip="Date the article was last updated/republished on WordPress. If the same as Published, the article has never been updated." />
          <SortHeader label="Idx" col="indexed" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-10 shrink-0 text-center" tooltip="Google indexation status via GSC URL Inspection API. Green = indexed and appearing in search results." />
          <SortHeader label="Pos." col="position" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-16 shrink-0 text-right" tooltip="Average Google search position (GSC, 28d). Lower is better: 1-3 = top of page 1, 4-10 = page 1, 11+ = page 2 or deeper." />
          <SortHeader label="Impr." col="impressions" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-16 shrink-0 text-right" tooltip="Times this article appeared in Google search results (GSC, 28d). High impressions + low clicks = title/description may need work." />
          <SortHeader label="Clicks" col="clicks" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-16 shrink-0 text-right" tooltip="Times a user clicked through from Google search results (GSC, 28d). The primary measure of organic search traffic." />
          <SortHeader label="CTR" col="ctr" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-14 shrink-0 text-right" tooltip="Click-through rate: clicks / impressions (GSC, 28d). Measures how compelling your title and meta description are in search results." />
          <span className="w-16 shrink-0 text-right cursor-help" title="Total visits to this page from all sources (GA4, 28d). Unlike GSC clicks, this includes direct, social, email, and referral traffic.">Sessions</span>
          <span className="w-14 shrink-0 text-right cursor-help" title="Percentage of sessions that were NOT engaged: lasted under 10 seconds, had no conversions, and viewed only one page (GA4, 28d). High bounce = visitors leaving immediately.">Bounce</span>
          <span className="w-12 shrink-0 text-right cursor-help" title="Number of key events triggered on this page (GA4, 28d). Key events are goals you define in GA4: form submissions, demo requests, sign-ups, etc.">Conv.</span>
          <SortHeader label="AI" col="ai" current={sortBy} order={order} onClick={onSortClick} disabled={loading} className="w-10 shrink-0 text-right" tooltip="Sessions referred from AI tools (ChatGPT, Perplexity, Gemini, Claude, Copilot, etc). Shows how much traffic this article gets from AI-generated recommendations." />
        </div>

        {/* Rows */}
        <div className={`border border-gray-200 rounded-b divide-y divide-gray-100 bg-white transition-opacity ${loading ? 'opacity-40' : ''}`}>
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center h-64" />
          ) : rows.length === 0 ? (
            <div className="text-center text-gray-400 py-12 text-sm">No articles match your filters.</div>
          ) : (
            rows.map((row) => <PerformanceRow key={row.url} row={row} onClick={() => onRowClick(row.url)} />)
          )}
        </div>

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white/90 rounded-lg shadow-lg border border-gray-200 px-5 py-3 flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-cyan-500" />
              <span className="text-sm text-gray-600 font-medium">Loading…</span>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-gray-500">
            Page {currentPage} of {totalPages} · showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => onOffsetChange(0)}
              disabled={!canPrev || loading}
              className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => onOffsetChange((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={!canPrev || loading}
              className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <button
              onClick={() => onOffsetChange((o) => o + PAGE_SIZE)}
              disabled={!canNext || loading}
              className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => onOffsetChange((totalPages - 1) * PAGE_SIZE)}
              disabled={!canNext || loading}
              className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </>
  );
};
